import type { MedplumClient } from '@medplum/core';
import type { Practitioner, Reference } from '@medplum/fhirtypes';
import {
  formatPractitionerPreferredLabel,
  formatPractitionerStaffLabel,
} from '@unify/fhir-model/resources/practitioner';

const ID_CHUNK = 50;

async function loadPractitionerLabelMap(
  medplum: Pick<MedplumClient, 'searchResources'>,
  ids: Iterable<string>,
  format: (p: Practitioner) => string,
): Promise<Map<string, string>> {
  const unique = [...new Set([...ids].map((id) => id.trim()).filter(Boolean))];
  const map = new Map<string, string>();
  for (let i = 0; i < unique.length; i += ID_CHUNK) {
    const chunk = unique.slice(i, i + ID_CHUNK);
    let found: Practitioner[];
    try {
      found = await medplum.searchResources('Practitioner', {
        _id: chunk.join(','),
        _count: String(chunk.length),
      }, { cache: 'no-cache' });
    } catch {
      continue;
    }
    for (const p of found) {
      const label = format(p);
      if (p.id && label) map.set(p.id, label);
    }
  }
  return map;
}

/** Chunked Practitioner?_id= search → preferred display labels. */
export function loadPractitionerPreferredLabelMap(
  medplum: Pick<MedplumClient, 'searchResources'>,
  ids: Iterable<string>,
): Promise<Map<string, string>> {
  return loadPractitionerLabelMap(medplum, ids, formatPractitionerPreferredLabel);
}

export function loadPractitionerStaffLabelMap(
  medplum: Pick<MedplumClient, 'searchResources'>,
  ids: Iterable<string>,
): Promise<Map<string, string>> {
  return loadPractitionerLabelMap(medplum, ids, formatPractitionerStaffLabel);
}

export function preferredLabelOrFallback(
  practitionerId: string | undefined,
  labels: Map<string, string>,
  storedDisplay: string | undefined,
  fallback = 'Provider',
): string {
  if (practitionerId) {
    const live = labels.get(practitionerId);
    if (live) return live;
  }
  const stored = storedDisplay?.trim();
  if (stored) return stored;
  return fallback;
}

/** Client-safe reference display for writes involving a loaded Practitioner. */
export function practitionerPreferredReference(
  practitioner: Practitioner,
): Reference<Practitioner> {
  const display = formatPractitionerPreferredLabel(practitioner);
  return {
    ...(practitioner.id ? { reference: `Practitioner/${practitioner.id}` } : {}),
    ...(display ? { display } : {}),
  };
}
